import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (userDoc.exists() && userDoc.data().role === 'admin') {
            setUserData({ ...userDoc.data(), role: 'admin' });
            setUser(firebaseUser);
          } else {
            await signOut(auth);
            setUser(null);
            setUserData(null);
            setError('Access denied. Admin accounts only.');
          }
        } catch {
          setError('Error verifying admin access');
          setUser(null);
          setUserData(null);
        }
      } else {
        setUser(null);
        setUserData(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const userDoc = await getDoc(doc(db, 'users', cred.user.uid));
      if (!userDoc.exists()) {
        await signOut(auth);
        setError(
          `No admin profile found for UID ${cred.user.uid}. In Firestore, create users/${cred.user.uid} with role "admin".`
        );
        setUser(null);
        setUserData(null);
        return;
      }
      if (userDoc.data().role !== 'admin') {
        await signOut(auth);
        setError(
          `Access denied. users/${cred.user.uid} exists but role is "${userDoc.data().role || 'missing'}", not "admin".`
        );
        setUser(null);
        setUserData(null);
        return;
      }
      setUserData({ ...userDoc.data(), role: 'admin' });
      setUser(cred.user);
    } catch (err) {
      console.error('Admin login failed:', err?.code, err?.message);
      if (err?.code === 'auth/operation-not-allowed') {
        setError(
          'Email/Password sign-in is disabled. Firebase Console → Authentication → Sign-in method → enable Email/Password.'
        );
      } else if (
        err?.code === 'auth/invalid-credential' ||
        err?.code === 'auth/wrong-password' ||
        err?.code === 'auth/user-not-found' ||
        err?.code === 'auth/invalid-email'
      ) {
        setError(
          `Invalid email or password (${err.code}). Use a user from darswebsite-5f2c8 → Authentication → Users, or add one there.`
        );
      } else if (err?.code === 'permission-denied') {
        setError('Firestore denied reading your user profile. Run: npm run deploy:firestore-rules');
      } else {
        setError(`${err?.message || 'Login failed'}${err?.code ? ` (${err.code})` : ''}`);
      }
      setUser(null);
      setUserData(null);
    }
    setLoading(false);
  };

  const resetPassword = async (email) => {
    setLoading(true);
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      setError('Password reset failed: ' + (err.message || 'Unknown error'));
    }
    setLoading(false);
  };

  const logout = async () => {
    setLoading(true);
    setError(null);
    try {
      await signOut(auth);
      setUser(null);
      setUserData(null);
    } catch {
      setError('Error logging out');
    }
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{ user, userData, loading, error, login, logout, resetPassword, isAdmin: userData?.role === 'admin' }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
